import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SearchCustomersDialogComponent } from '../search-customers-dialog/search-customers-dialog.component';
import { CustomerInformation } from '../../../../models/dtos/Classes/CustomerInformation';
import { CommonDataService } from '../../../../services/common-data.service';
import { take, Subject, takeUntil } from 'rxjs';
import { ISearchCustomersData } from '../../../../models/common/ISearchCustomersData';
import { CustomerSearchType, OtherMetaData } from '../../../../models/dtos';
import { MatCheckbox } from '@angular/material/checkbox';
import { CONSTANTS } from '../../../../Constants/Constants';
import { CRMEntityDetails } from '../../../../models/common/CRMEntityDetails';

@Component({
  selector: 'app-customer-information-dialog',
  imports: [
    MatDialogClose,
    TranslatePipe,
    CommonModule,
    MatButtonModule,
    MatIcon,
    MatCheckbox
  ],
  templateUrl: './customer-information-dialog.component.html',
  styleUrl: './customer-information-dialog.component.scss'
})
export class CustomerInformationDialogComponent implements OnInit, OnDestroy {

  @Output() cancelClick = new EventEmitter<void>();
  @Input() isEstimateSapSubmitted: boolean = false;
  private readonly destroy$ = new Subject<void>();
  isReadOnlyMode: boolean = false;
  private isApplyingSharedData: boolean = false;
  private lastSavedMetaData: OtherMetaData | null = null;

  constructor(private readonly dialog: MatDialog,
    private readonly commonDataService: CommonDataService) { }

  selectedSection: string = CONSTANTS.ACCOUNT_NAME_KEY;
  accountNameData: CustomerInformation = new CustomerInformation();
  shipToLocationData: CustomerInformation = new CustomerInformation();
  billToLocationData: CustomerInformation = new CustomerInformation();
  endUserData: CustomerInformation = new CustomerInformation();
  otherMetaData: OtherMetaData = new OtherMetaData();
  applySoldToSection: Record<string, boolean> = {
    [CONSTANTS.SHIP_TO_KEY]: false,
    [CONSTANTS.BILL_TO_KEY]: false,
    [CONSTANTS.END_USER_KEY]: false
  };

  customerData: CRMEntityDetails[] = [];

  ngOnInit(): void {
    this.otherMetaData = this.commonDataService.getOtherMetaData();
    if (this.otherMetaData) {
      this.otherMetaData = structuredClone(this.otherMetaData);
      this.accountNameData = this.otherMetaData.customerInformation ?? new CustomerInformation();
      this.shipToLocationData = this.otherMetaData.shipToAccount ?? new CustomerInformation();
      this.billToLocationData = this.otherMetaData.billToAccount ?? new CustomerInformation();
      this.endUserData = this.otherMetaData.endUserAccount ?? new CustomerInformation();
    }
    this.lastSavedMetaData = structuredClone(this.otherMetaData);
    this.evaluateApplySoldToSections();

    this.prefillCustomerData();
    this.subscribeToSharedDataChanges();
    this.setupReadOnlyMode();
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.readOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isReadOnly => {
        this.isReadOnlyMode = isReadOnly || this.isEstimateSapSubmitted;
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.isEstimateSapSubmitted = false;
  }

  selectSection(section: string): void {
    this.selectedSection = section;
    this.prefillCustomerData();
  }

  prefillCustomerData() {
    switch (this.selectedSection) {
      case CONSTANTS.ACCOUNT_NAME_KEY:
        this.customerData = this.mapCustomerInformationToLabelValue(this.accountNameData);
        break;
      case CONSTANTS.SHIP_TO_KEY:
        this.customerData = this.mapCustomerInformationToLabelValue(this.shipToLocationData);
        break;
      case CONSTANTS.BILL_TO_KEY:
        this.customerData = this.mapCustomerInformationToLabelValue(this.billToLocationData);
        break;
      case CONSTANTS.END_USER_KEY:
        this.customerData = this.mapCustomerInformationToLabelValue(this.endUserData);
        break;
    }
  }

  mapCustomerInformationToLabelValue(customerInfo: CustomerInformation): CRMEntityDetails[] {
    return [
      { label: 'SALESFORCE_ID', value: customerInfo.customerSalesforceId || '' },
      { label: 'SAP_NUMBER', value: customerInfo.customerID || '' },
      { label: 'NAME', value: customerInfo.customerName || '' },
      { label: 'ADDRESS_1', value: customerInfo.custAddress1 || '' },
      { label: 'ADDRESS_2', value: customerInfo.custAddress2 || '' },
      { label: 'CITY', value: customerInfo.custCity || '' },
      { label: 'STATE', value: customerInfo.custState || '' },
      { label: 'ZIP_CODE', value: customerInfo.custZipCode || '' }
    ];
  }

  getCustomerSearchType(): CustomerSearchType {
    switch (this.selectedSection) {
      case CONSTANTS.ACCOUNT_NAME_KEY:
        return CustomerSearchType.AccountName;
      case CONSTANTS.SHIP_TO_KEY:
        return CustomerSearchType.ShipToLocation;
      case CONSTANTS.BILL_TO_KEY:
        return CustomerSearchType.BillToLocation;
      case CONSTANTS.END_USER_KEY:
        return CustomerSearchType.EndUser;
      default:
        return CustomerSearchType.AccountName;
    }
  }

  searchCustomers() {
    if (this.isReadOnlyMode) {
      return;
    }
    const dialogRef = this.dialog.open(SearchCustomersDialogComponent, {
      width: '700px',
      data: this.getCustomerSearchType(),
      disableClose: true
    });

    dialogRef.afterClosed()
    .pipe(take(1))
    .subscribe((result: { customerInfo: ISearchCustomersData }) => {
      if(result?.customerInfo){
        const customerInfo = this.convertSearchCustomerDataToCustomerInformation(result.customerInfo);
        this.assignCustomerInfoToSelectedSection(customerInfo);
        this.prefillCustomerData();
      }
    });
  }

  convertSearchCustomerDataToCustomerInformation(searchData: ISearchCustomersData): CustomerInformation {
    const customerInfo = new CustomerInformation();
    customerInfo.customerSalesforceId = searchData.salesForceId || '';
    customerInfo.customerID = searchData.sapNumber || '';
    customerInfo.customerName = searchData.customerName || '';
    customerInfo.custAddress1 = searchData.address1 || '';
    customerInfo.custAddress2 = '';
    customerInfo.custCity = searchData.city || '';
    customerInfo.custState = searchData.state || '';
    customerInfo.custZipCode = searchData.zipCode || '';
    return customerInfo;
  }

  assignCustomerInfoToSelectedSection(customerInfo: CustomerInformation): void {
    switch (this.selectedSection) {
      case CONSTANTS.ACCOUNT_NAME_KEY:
        this.accountNameData = customerInfo;
        this.applySoldToAccountToCheckedSections();
        break;
      case CONSTANTS.SHIP_TO_KEY:
        this.shipToLocationData = customerInfo;
        this.applySoldToSection[CONSTANTS.SHIP_TO_KEY] = this.isCustomerAccountSameAsSoldTo(customerInfo);
        break;
      case CONSTANTS.BILL_TO_KEY:
        this.billToLocationData = customerInfo;
        this.applySoldToSection[CONSTANTS.BILL_TO_KEY] = this.isCustomerAccountSameAsSoldTo(customerInfo);
        break;
      case CONSTANTS.END_USER_KEY:
        this.endUserData = customerInfo;
        this.applySoldToSection[CONSTANTS.END_USER_KEY] = this.isCustomerAccountSameAsSoldTo(customerInfo);
        break;
      default:
        this.accountNameData = customerInfo;
        break;
    }
  }
  saveCustomerInfo(){
    if (this.isReadOnlyMode) {
      return;
    }

    if (this.hasCustomerDataChanged()) {
      this.commonDataService.isSaveEnabled = true;
    }
    this.evaluateApplySoldToSections();
    this.publishCustomerDataChange();
    this.onCancel();
  }

  enableBtn(): boolean {
    return this.selectedSection !== CONSTANTS.ACCOUNT_NAME_KEY;
  }

  clearData(): void {
    if (this.isReadOnlyMode) {
      return;
    }

    switch (this.selectedSection) {
      case CONSTANTS.SHIP_TO_KEY:
        this.shipToLocationData = new CustomerInformation();
        this.applySoldToSection[CONSTANTS.SHIP_TO_KEY] = false;
        break;
      case CONSTANTS.BILL_TO_KEY:
        this.billToLocationData = new CustomerInformation();
        this.applySoldToSection[CONSTANTS.BILL_TO_KEY] = false;
        break;
      case CONSTANTS.END_USER_KEY:
        this.endUserData = new CustomerInformation();
        this.applySoldToSection[CONSTANTS.END_USER_KEY] = false;
        break;
    }
    this.prefillCustomerData();
  }

  private subscribeToSharedDataChanges(): void {
    this.commonDataService.otherMetaDataChanged$
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (sharedMetaData) => {
      if(!sharedMetaData || this.isApplyingSharedData){return;}
      const isSameData = 
      this.getCustomerSignature(this.accountNameData) === this.getCustomerSignature(sharedMetaData?.customerInformation) &&
      this.getCustomerSignature(this.shipToLocationData) === this.getCustomerSignature(sharedMetaData?.shipToAccount) &&
      this.getCustomerSignature(this.billToLocationData) === this.getCustomerSignature(sharedMetaData?.billToAccount) &&
      this.getCustomerSignature(this.endUserData) === this.getCustomerSignature(sharedMetaData?.endUserAccount);
      if(isSameData){ return; }
      this.otherMetaData= structuredClone(sharedMetaData);
      this.accountNameData = this.otherMetaData.customerInformation ?? new CustomerInformation();
      this.shipToLocationData = this.otherMetaData.shipToAccount ?? new CustomerInformation();
      this.billToLocationData = this.otherMetaData.billToAccount ?? new CustomerInformation();
      this.endUserData = this.otherMetaData.endUserAccount ?? new CustomerInformation();
      this.lastSavedMetaData = structuredClone(this.otherMetaData);
      this.evaluateApplySoldToSections();
      this.prefillCustomerData();
    },
    error: () => {
      this.prefillCustomerData();
    }
  });
  }
  hasSoldToAccountSelected(): boolean {
    return !!this.accountNameData.customerSalesforceId?.trim();
  }
  shouldShowApplySoldToCheckbox(): boolean {
    return this.selectedSection !== CONSTANTS.ACCOUNT_NAME_KEY;
  }
  isApplySoldToChecked(): boolean {
    return this.applySoldToSection[this.selectedSection] ?? false;
  }
  onApplySoldToChange(isChecked: boolean): void {
    if (this.isReadOnlyMode || this.selectedSection === CONSTANTS.ACCOUNT_NAME_KEY) {
      return;
    }
    this.applySoldToSection[this.selectedSection] = isChecked;
    if (isChecked && this.hasSoldToAccountSelected()) {
      this.assignSoldDataToSection(this.selectedSection);
      this.prefillCustomerData();
    }
  }
  private assignSoldDataToSection(section: string): void {
    const soldToData = this.cloneData(this.accountNameData);
    switch (section) {
      case CONSTANTS.SHIP_TO_KEY: this.shipToLocationData = soldToData; break;
      case CONSTANTS.BILL_TO_KEY: this.billToLocationData = soldToData; break;
      case CONSTANTS.END_USER_KEY: this.endUserData = soldToData; break;
    }
  }
  private applySoldToAccountToCheckedSections(): void {
    if (!this.hasSoldToAccountSelected()) {
      this.evaluateApplySoldToSections();
      return;
    }
    if (this.applySoldToSection[CONSTANTS.SHIP_TO_KEY]) {
      this.assignSoldDataToSection(CONSTANTS.SHIP_TO_KEY);
    }
    if (this.applySoldToSection[CONSTANTS.BILL_TO_KEY]) {
      this.assignSoldDataToSection(CONSTANTS.BILL_TO_KEY);
    }
    if (this.applySoldToSection[CONSTANTS.END_USER_KEY]) {
      this.assignSoldDataToSection(CONSTANTS.END_USER_KEY);
    }
    this.evaluateApplySoldToSections();
  }

  private evaluateApplySoldToSections(): void {
    this.applySoldToSection[CONSTANTS.SHIP_TO_KEY] =
      this.isCustomerAccountSameAsSoldTo(this.shipToLocationData);

    this.applySoldToSection[CONSTANTS.BILL_TO_KEY] =
      this.isCustomerAccountSameAsSoldTo(this.billToLocationData);

    this.applySoldToSection[CONSTANTS.END_USER_KEY] =
      this.isCustomerAccountSameAsSoldTo(this.endUserData);
  }
  private isCustomerAccountSameAsSoldTo(customerInfo: CustomerInformation): boolean {
    return !!customerInfo?.customerSalesforceId?.trim()
      && this.getCustomerSignature(customerInfo) === this.getCustomerSignature(this.accountNameData);
  }
  public cloneData(data: CustomerInformation): CustomerInformation {
    return Object.assign(new CustomerInformation(), data);
  }
  private getCustomerSignature(customerInfo: CustomerInformation | null | undefined): string {
    if (!customerInfo) { return ''; }
    return `${customerInfo.customerSalesforceId}|${customerInfo.customerID}`;
  }

  private hasCustomerDataChanged(): boolean {
    return (
      this.getCustomerSignature(this.accountNameData) !== this.getCustomerSignature(this.lastSavedMetaData?.customerInformation) ||
      this.getCustomerSignature(this.shipToLocationData) !== this.getCustomerSignature(this.lastSavedMetaData?.shipToAccount) ||
      this.getCustomerSignature(this.billToLocationData) !== this.getCustomerSignature(this.lastSavedMetaData?.billToAccount) ||
      this.getCustomerSignature(this.endUserData) !== this.getCustomerSignature(this.lastSavedMetaData?.endUserAccount)
    );
  }

  private publishCustomerDataChange() : void{
    this.isApplyingSharedData = true;
    const publishOtherMetaData = this.commonDataService.getOtherMetaData() ?? new OtherMetaData();
    publishOtherMetaData.customerInformation = this.cloneData(this.accountNameData);
    publishOtherMetaData.shipToAccount = this.cloneData(this.shipToLocationData);
    publishOtherMetaData.billToAccount = this.cloneData(this.billToLocationData);
    publishOtherMetaData.endUserAccount = this.cloneData(this.endUserData);
    this.commonDataService.patchOtherMetaData(publishOtherMetaData);
    this.lastSavedMetaData = structuredClone(publishOtherMetaData);
    this.isApplyingSharedData = false;
  }

  onCancel(): void {
    this.cancelClick.emit();
  }
}
